import React, { useState, useEffect, useMemo } from 'react'
import { Card, Text, Text3 } from '../common'
import { useNavigate } from 'react-router-dom'
import Modal2 from '@/components/Modal2'
import { Message } from '@cx/ui'
import { cancelOrder } from '@/api'
import { Box, Stack, alpha, Divider, Button, useTheme } from '@mui/material'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import DownloadContractModal from './DownloadContractModal'
import { OrderGoodsDetail, OrderStatusEnum, OrderPayTypeEnum, InvoiceStatusEnum } from '@/types'
import { getDurationString } from './utils'
import { ensureSecurityGate } from '@/utils/securityGate'

interface OrderCardProps {
  item: any
  setApplyInvoiceModalVisible: (visible: boolean) => void
  setCurrentOrder: (order: any) => void
  setReceiptModalVisible: (visible: boolean) => void
}

export const PAY_TYPE_TO_TEXT = {
  [OrderPayTypeEnum.alipay]: '支付宝',
  [OrderPayTypeEnum.public]: '对公支付',
  [OrderPayTypeEnum.voucher]: '兑换码支付',
}

export const formatGoodsDetails = (detail: string) => {
  const durations = { D: '日', M: '月', Y: '年' }
  let goodsDetail
  try {
    goodsDetail = JSON.parse(detail) as OrderGoodsDetail
  } catch (e) {}
  if (goodsDetail) {
    const details: { name: string; value: string }[] = [
      // {
      //   name: goodsDetail?.package_name,
      //   value: `${goodsDetail?.duration || ''} ${durations[goodsDetail?.purchase_duration_unit] || ''}`,
      // },
    ]
    goodsDetail?.items?.forEach((item) => details.push(item))
    return details
  } else {
    return [{ name: '', value: '' }]
  }
}

const OrderCard: React.FC<OrderCardProps> = ({
  item,
  setReceiptModalVisible,
  setApplyInvoiceModalVisible,
  setCurrentOrder,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [visible, setVisible] = useState(false)

  const [endTime, setEndTime] = useState(item.expire_time_seconds || -1)

  useEffect(() => {
    const countDown = setInterval(() => {
      if (endTime > 0) {
        setEndTime((second: number) => {
          if (second <= 1) {
            navigate(0)
            return 0
          } else {
            return second - 1
          }
        })
      } else {
        clearInterval(countDown)
      }
    }, 1000)

    return () => {
      clearInterval(countDown)
    }
  }, [])

  const statusMap = {
    [OrderStatusEnum.unpaid]: {
      text: '未支付',
      icon: ErrorRoundedIcon,
      color: theme.palette.warning.main,
    },
    [OrderStatusEnum.toBeConfirmed]: {
      text: '未支付',
      icon: ErrorRoundedIcon,
      color: theme.palette.warning.main,
    },
    [OrderStatusEnum.paid]: {
      text: '支付成功',
      icon: CheckCircleRoundedIcon,
      color: theme.palette.success.main,
    },
    [OrderStatusEnum.refunded]: {
      text: '已退款',
      icon: CancelRoundedIcon,
      color: 'rgba(0,0,0,0.2)',
    },
    [OrderStatusEnum.canceled]: {
      text: '已取消',
      icon: CancelRoundedIcon,
      color: 'rgba(0,0,0,0.2)',
    },
    [OrderStatusEnum.expaired]: {
      text: '超时关闭',
      icon: CancelRoundedIcon,
      color: 'rgba(0,0,0,0.2)',
    },
    [OrderStatusEnum.auditing]: {
      text: '等待审核对公信息',
      icon: ErrorRoundedIcon,
      color: theme.palette.primary.main,
    },
  }

  const currentStatus = statusMap[item.status as keyof typeof statusMap] || {
    text: '未知',
    icon: CancelRoundedIcon,
    color: 'rgba(0,0,0,0.2)',
  }

  const onCancelOrder = () => {
    Modal2.confirm({
      title: '提示',
      content: '确认关闭订单？',
      onOk: () =>
        cancelOrder({ order_code: item.order_code })
          .then(() => {
            navigate(0)
            Message.success('订单关闭成功')
          })
          .catch((e) => {
            navigate(0)
          }),
    })
  }

  const goodsDetail = useMemo(() => {
    return formatGoodsDetails(item.goods_detail)
  }, [item.goods_detail])

  const hasDiscountCoupon = useMemo(() => {
    if (item && ((item.referral_code?.saved_amount || 0) > 0 || (item.coupon_code?.saved_amount || 0) > 0)) {
      return true
    }
    return false
  }, [item])

  return (
    <Card key={item.order_code}>
      <DownloadContractModal
        open={visible}
        onOk={() => setVisible(false)}
        onCancel={() => setVisible(false)}
        data={item}
      />
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Stack direction='row' alignItems='center' gap={0.5}>
          <Box component='img' src={item.logo_url} sx={{ height: 24 }}></Box>
          <Text sx={{ fontWeight: 'bold', fontSize: '20px' }}>{item.app_name}</Text>
        </Stack>
        <Stack direction='row'>
          {[OrderStatusEnum.unpaid, OrderStatusEnum.toBeConfirmed].includes(item.status) && (
            <Button size='small' onClick={() => navigate(`/console/order?order_code=${item.order_code}`)}>
              立即支付
            </Button>
          )}

          {[OrderStatusEnum.unpaid, OrderStatusEnum.toBeConfirmed, OrderStatusEnum.auditing].includes(item.status) && (
            <Button size='small' onClick={onCancelOrder}>
              关闭订单
            </Button>
          )}

          {item.status === OrderStatusEnum.paid && (
            <>
              {InvoiceStatusEnum.unApply === item.invoice.status && (
                <Button
                  size='small'
                  onClick={async () => {
                    if (!(await ensureSecurityGate('order_invoice'))) return
                    setCurrentOrder(item)
                    setTimeout(() => {
                      setApplyInvoiceModalVisible(true)
                    })
                  }}
                >
                  申请发票
                </Button>
              )}
              {InvoiceStatusEnum.rejected === item.invoice.status && (
                <Button
                  size='small'
                  onClick={async () => {
                    if (!(await ensureSecurityGate('order_invoice'))) return
                    setCurrentOrder(item)
                    setTimeout(() => {
                      setApplyInvoiceModalVisible(true)
                    })
                  }}
                >
                  重新申请发票
                </Button>
              )}
              {InvoiceStatusEnum.applying === item.invoice.status && (
                <Button size='small' disabled>
                  发票申请中
                </Button>
              )}
              {InvoiceStatusEnum.applied === item.invoice.status && (
                <Button
                  size='small'
                  onClick={async () => {
                    if (!(await ensureSecurityGate('order_invoice'))) return
                    window.open(item.invoice.invoice_url, '_blank')
                  }}
                >
                  下载发票
                </Button>
              )}
            </>
          )}

          {item.has_contract &&
            [OrderStatusEnum.unpaid, OrderStatusEnum.paid, OrderStatusEnum.auditing].includes(item.status) && (
              <Button
                size='small'
                onClick={async () => {
                  if (!(await ensureSecurityGate('order_contract'))) return
                  setVisible(true)
                }}
              >
                下载合同
              </Button>
            )}
        </Stack>
      </Stack>
      <Stack direction='row' gap={3} sx={{ mt: 2 }}>
        <Stack
          direction='row'
          alignItems='center'
          gap={0.5}
          sx={{
            fontSize: 12,
            color: currentStatus.color,
            backgroundColor: alpha(currentStatus.color, 0.1),
            borderRadius: 0.5,
            py: '2px',
            px: 1,
            lineHeight: 1,
          }}
        >
          <currentStatus.icon sx={{ fontSize: 16 }}></currentStatus.icon>
          <Box>{currentStatus.text}</Box>
        </Stack>
        {endTime > 0 && [OrderStatusEnum.unpaid, OrderStatusEnum.toBeConfirmed].includes(item.status) && (
          <Stack direction='row' alignItems='center' gap={0.5} sx={{ fontSize: 12 }}>
            <ErrorRoundedIcon sx={{ color: 'error.main', fontSize: 16 }} />
            <Box>
              支付剩余{' '}
              <Box component='span' sx={{ color: 'error.main' }}>
                {getDurationString(endTime)}
              </Box>
              ，否则订单自动关闭
            </Box>
          </Stack>
        )}

        {item.status === OrderStatusEnum.expaired && (
          <Stack direction='row' alignItems='center' gap={0.5} sx={{ fontSize: 12 }}>
            <ErrorRoundedIcon sx={{ color: 'error.main', fontSize: 16 }} />
            <Box>订单已过期，请重新下单</Box>
          </Stack>
        )}

        {item.status === OrderStatusEnum.canceled && (
          <Stack direction='row' alignItems='center' gap={0.5} sx={{ fontSize: 12 }}>
            <ErrorRoundedIcon sx={{ color: 'error.main', fontSize: 16 }} />
            <Box>订单已取消，请重新下单</Box>
          </Stack>
        )}
      </Stack>
      <Divider sx={{ my: 3, borderColor: '#eee' }} />
      <Stack direction='row' gap={3} justifyContent='space-between'>
        <Stack direction='row' alignItems='center'>
          <Text3 sx={{ width: 74 }}>订单号</Text3>
          <Text sx={{ fontSize: 14 }}>{item.order_code}</Text>
        </Stack>
        <Stack direction='row' alignItems='center'>
          <Text3 sx={{ width: 74 }}>创建时间</Text3>
          <Text sx={{ fontSize: 14 }}>{item.created_at}</Text>
        </Stack>

        <Stack direction='row' alignItems='center' sx={{ minWidth: 212 }}>
          {item.status === OrderStatusEnum.paid && (
            <>
              <Text3 sx={{ width: 74 }}>支付时间</Text3>
              <Text sx={{ fontSize: 14 }}>{item.updated_at}</Text>
            </>
          )}
        </Stack>
      </Stack>
      <Stack direction='row' alignItems='center' sx={{ mt: 3, mb: 2 }}>
        <Text3 sx={{ width: 74 }}>{item.status === OrderStatusEnum.paid ? '实付款' : '需付款'} </Text3>
        <Text sx={{ fontWeight: 600, fontSize: 14 }}>
          ￥{' '}
          {[OrderStatusEnum.paid, OrderStatusEnum.unpaid, OrderStatusEnum.auditing].includes(item.status)
            ? item.real_amount
            : item.total_amount}
        </Text>
      </Stack>
      {hasDiscountCoupon && (
        <Stack
          gap={3}
          sx={{
            p: 2,
            backgroundColor: '#F2F3F5',
            borderRadius: 1,
          }}
        >
          <Stack direction='row'>
            <Text sx={{ width: 98, fontSize: 14 }}>总金额</Text>
            <Text sx={{ width: 98, fontSize: 14, mr: 15 }}>￥{item.total_amount}</Text>
          </Stack>
          {item.coupon_code.saved_amount > 0 && (
            <Stack direction='row'>
              <Text sx={{ width: 98, fontSize: 14 }}>优惠券</Text>
              <Text sx={{ width: 98, fontSize: 14, mr: 15, color: 'error.main' }}>
                -￥{item.coupon_code.saved_amount.toFixed(2)}
              </Text>
              <Text3
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.coupon_code.coupon_code}
              </Text3>
            </Stack>
          )}
          {item.referral_code.saved_amount > 0 && (
            <Stack direction='row'>
              <Text sx={{ width: 98, fontSize: 14 }}>推荐码</Text>
              <Text sx={{ width: 98, fontSize: 14, mr: 15, color: 'error.main' }}>
                -￥{item.referral_code.saved_amount.toFixed(2)}
              </Text>
              <Text3>{item.referral_code.referral_code}</Text3>
            </Stack>
          )}
        </Stack>
      )}
      {(item.status === OrderStatusEnum.paid || item.pay_type === OrderPayTypeEnum.public) &&
        PAY_TYPE_TO_TEXT[item.pay_type as keyof typeof PAY_TYPE_TO_TEXT] && (
          <Stack direction='row' alignItems='center' sx={{ mt: 3, mb: 2 }}>
            <Text3 sx={{ width: 74 }}>付款方式</Text3>
            <Text sx={{ fontSize: 14, mr: 3 }}>{PAY_TYPE_TO_TEXT[item.pay_type as keyof typeof PAY_TYPE_TO_TEXT]}</Text>

            {item.pay_type === OrderPayTypeEnum.public && (
              <Text
                sx={{ fontSize: 14, color: 'primary.main', cursor: 'pointer' }}
                onClick={() => {
                  setCurrentOrder(item)
                  setTimeout(() => {
                    setReceiptModalVisible(true)
                  })
                }}
              >
                查看对公回执单
              </Text>
            )}
          </Stack>
        )}

      <Stack direction='row' alignItems='center' sx={{ mt: 3, mb: 2 }}>
        <Text3 sx={{ width: 74 }}>套餐内容</Text3>
        <Text sx={{ fontSize: 14, mr: 3 }}>{goodsDetail?.map((i) => `${i?.name} - ${i?.value}`).join('、')}</Text>
      </Stack>
    </Card>
  )
}

export default OrderCard
